import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useIsMobile } from "senf-atomic-design-system";
import { useModals } from "senf-atomic-design-system/src/components/molecules/modalStack/ModalProvider";
import { AuthModal, useAuthContext } from "senf-shared";
import { likeIdea, unlikeIdea } from "../redux/actions/likeActions";
import { RESET_PROFILE_PAGE, SET_ERRORS, SET_IDEA } from "../redux/types";

import Survey from "../pages/Survey";
import type { ClientState } from "../redux/reducers/dataReducer";

export const useHandles = (
  projectId: string,
  ideaMapId: string,
  ideaId: string,
) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [shouldSnapToTop, setShouldSnapToTop] = useState(false);

  const { openModal, closeModal, closeAllModals } = useModals();
  const { user, signOut, handleLike: userHandleLike } = useAuthContext();
  const project = useSelector((state: ClientState) => state.data.project);

  const setAuthEditOpen = useCallback(() => {
    if (!user?.authenticated && !user?.emailVerified) {
      openModal(<AuthModal authVerifyEmail={true} project={project} />, {
        swipe: !!isMobile,
        size: isMobile && "fullscreen",
      });

      return;
    }
    openModal(<AuthModal authAddDetails={true} project={project} />, {
      swipe: !!isMobile,
      size: isMobile && "fullscreen",
    });
  }, [isMobile, user?.authenticated, user?.emailVerified]);

  const login = useCallback(() => {
    if (!user?.authenticated) {
      openModal(<AuthModal authOptions={true} project={project} />, {
        swipe: !!isMobile,
        size: isMobile && "fullscreen",
      });
    }
  }, [isMobile, navigate, user?.userId, projectId]);

  const logout = useCallback(() => {
    const openDiscardModal = () => {
      openModal(null, {
        title: t("global:guestLogout"),
        description: t("global:guestLogoutDescription"),
        swipe: !!isMobile,
        size: isMobile ? "full" : "md",
        cancelText: t("global:cancel"),
        submitText: t("auth:logout"),
        critical: true,
        onSubmit: () => {
          signOut().then(() => {
            dispatch({ type: RESET_PROFILE_PAGE });
            openModal(<AuthModal project={project} />, {
              swipe: !!isMobile,
              size: isMobile && "fullscreen",
            });
          });
        },
      });
    };
    if (user.isAnonymous) {
      openDiscardModal();
      return;
    }
    signOut().then(() => {
      dispatch({ type: RESET_PROFILE_PAGE });
      openModal(<AuthModal project={project} />, {
        swipe: !!isMobile,
        size: isMobile && "fullscreen",
      });
    });
  }, [isMobile, signOut, dispatch]);

  const openMyAccount = useCallback(() => {
    if (!user?.authenticated) {
      openModal(<AuthModal authOptions={true} project={project} />, {
        swipe: !!isMobile,
        size: isMobile && "fullscreen",
      });
      return;
    }
    if (!user?.authenticated && !user?.emailVerified) {
      openModal(<AuthModal authVerifyEmail={true} />, {
        swipe: !!isMobile,
        size: isMobile && "fullscreen",
      });
      return;
    }

    navigate(`?profile=${user?.userId}`);
  }, [isMobile, navigate, user?.userId, ideaId, projectId]);

  const openFeedback = useCallback(
    (type) => {
      let surveyId;
      if (type === "general") {
        surveyId = "ro8bJTitxE767COIQzR4";
      }
      if (type === "addIdea") {
        surveyId = "i9NOQh1dcVp8fSxyr7Od";
      }

      openModal(
        <Survey
          organizationId={"k5XMGl8qMSCDHyjmKDYg"}
          projectId={"uCdAXAtpRKMA2lPTyzrT"}
          surveyId={surveyId}
          repeatable={true}
        />,
        {
          id: "survey",
          swipe: !!isMobile,
          size: isMobile && "fullscreen",
          transform: false,
        },
      );
    },
    [isMobile],
  );

  const openCard = useCallback(
    (event, cardType, cardId) => {
      if (cardType === "ideaCard") {
        // trackId
        // window?.umami?.("list_openIdeaClick");
        const rootProfileId = searchParams.get("profile");
        if (rootProfileId) {
          navigate(
            `/projects/${projectId}/ideaMaps/${ideaMapId}/${cardId}?rootProfileId=${rootProfileId}`,
          );
        } else {
          navigate(`/projects/${projectId}/ideaMaps/${ideaMapId}/${cardId}`);
        }
      } else if (cardType === "projectCard") {
        navigate(`/projects/${cardId}/info`);
      }
    },
    [projectId],
  );

  const handleLike = async (project, ideaId) => {
    try {
      const like = await userHandleLike(ideaId);
      const action = like ? likeIdea : unlikeIdea;
      await dispatch(action(project, ideaMapId, ideaId, user));
      return like;
    } catch (error) {
      dispatch({
        type: SET_ERRORS,
        payload: {
          message: error.message,
        },
      });

      return false;
    }
  };

  const buttonLike = async (event, ideaId) => {
    event.stopPropagation();

    if (
      user?.userId &&
      (project?.settings?.demographicData?.collect === false ||
        user?.profileCompleted)
    )
      return handleLike(project, ideaId);
    openModal(
      <AuthModal
        authAddDetails={user?.userId && !user?.profileCompleted}
        project={project}
      />,
      {
        swipe: !!isMobile,
        size: isMobile && "fullscreen",
        // afterClose: () => handleLike(ideaId),
      },
    );
  };
  const buttonComment = (event, currentIdeaId) => {

    event.stopPropagation();
    if (!user?.authenticated) {
      openModal(<AuthModal project={project} />, {
        swipe: !!isMobile,
        size: isMobile && "fullscreen",
      });
      return;
    }
    if (!ideaId) {
      navigate(`${currentIdeaId}`);
    }

    setShouldSnapToTop(false);
    setTimeout(() => {
      setShouldSnapToTop(true);
    }, 1100);
    
    if(isMobile) {
      setTimeout(() => {
        document.getElementById("ideaDetailPage.commentInput")?.focus();
      }, 1500);
    } else {
      setTimeout(() => {
        document.getElementById("ideaDetailPage.commentInput")?.focus();
      }, 500);
    } 
  };

  const clickIdeaMarker = (id) => {
    dispatch({ type: SET_IDEA, payload: null });

    navigate(`/projects/${projectId}/ideaMaps/${ideaMapId}/${id}`);

    // trackId
    // window?.umami?.("map_openIdeaClick");
  };

  const openAddIdea = useCallback(
    (ideaMap) => {
      if (ideaMap) {
        closeAllModals();
        navigate(
          `/projects/${project.projectId}/ideaMaps/${
            ideaMap?.ideaMapId || ideaMapId
          }/addIdea`,
        );
        return;
      }
      if (pathname.indexOf("editIdea") > -1) {
        navigate(`${pathname.split("/").slice(0, -1).join("/")}`);

        return;
      }
      navigate(
        `/projects/${project.projectId}/ideaMaps/${
          ideaMap.ideaMapId || ideaMapId
        }`,
      );
    },
    [pathname],
  );

  const openInfo = () => {
    navigate(`/projects/${project.projectId}/info`);
    closeAllModals();
  };
  const openMap = (ideaMap) => {
    navigate(`/projects/${project.projectId}/ideaMaps/${ideaMap.ideaMapId}`);
  };

  const openSurvey = (surveyId: string) => {
    closeAllModals();

    navigate(`/projects/${project.projectId}/survey/${surveyId}`);
  };

  return {
    setAuthEditOpen,
    login,
    logout,
    openMyAccount,
    openFeedback,
    openCard,
    buttonLike,
    buttonComment,
    clickIdeaMarker,
    openAddIdea,
    openInfo,
    openMap,
    openSurvey,
    shouldSnapToTop,
    setShouldSnapToTop,
  };
};

export default useHandles;
